import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  comms,
  type LiveKitToken,
  type LiveKitTokenRequest,
} from '@/api/Comms';
import { useComms } from '../context/CommsContext';
import type { MatrixRtcPresence, MatrixRtcSessionHandle } from '../matrix/rtc';
import { CallContext, type CallContextValue } from './CallContext';
import { isValidCallRecordWriter } from './callProtocol';
import {
  initialCallState,
  reduceCall,
  type CallAction,
  type CallEffect,
  type CallState,
} from './callState';
import { LiveKitCallMedia } from './LiveKitCallMedia';

interface ApiResponse<T> {
  success: boolean;
  code: number;
  data?: T;
}

interface LiveKitTokenApi {
  getLiveKitToken(
    request: LiveKitTokenRequest,
  ): Promise<ApiResponse<LiveKitToken>>;
}

interface CallProviderProps extends PropsWithChildren {
  api?: LiveKitTokenApi;
  liveKitServiceUrl?: string;
  createCallId?(): string;
  onReturnToConversation?(operatorId: number, roomId: string): void;
}

interface ActiveRtcSession {
  operationNumber: number;
  handle: MatrixRtcSessionHandle;
}

interface MediaSession {
  callId: string;
  operationNumber: number;
  peerId: string;
  roomId: string;
  token: string;
  url: string;
}

type TerminalWriteEffect = Extract<CallEffect, { type: 'write-terminal' }>;

const defaultLiveKitServiceUrl = () => {
  const baseUrl =
    process.env.VITE_APP_BASE_API_URL ?? `${window.location.origin}/api/`;
  return new URL('comms/livekit-token', baseUrl).toString();
};

const defaultCreateCallId = () => {
  const randomId = globalThis.crypto?.randomUUID?.();
  if (!randomId) throw new Error('Call identity is unavailable.');
  return `gbacall_${randomId.replace(/-/g, '')}`;
};

const statusFor = (state: CallState): CallContextValue['status'] => {
  if (state.phase === 'incoming-ringing') return 'ringing';
  if (state.phase === 'outgoing-ringing' || state.phase === 'connecting') {
    return 'connecting';
  }
  if (state.phase === 'active') return 'active';
  if (state.phase === 'reconnecting') return 'reconnecting';
  return state.phase;
};

export const CallProvider = ({
  api = comms,
  liveKitServiceUrl = defaultLiveKitServiceUrl(),
  createCallId = defaultCreateCallId,
  onReturnToConversation,
  children,
}: CallProviderProps) => {
  const {
    status: commsStatus,
    matrixUserId,
    directRooms,
    joinRtcCall,
    sendCallTerminal,
    watchRoomCall,
  } = useComms();
  const [state, setState] = useState<CallState>(initialCallState);
  const [media, setMedia] = useState<MediaSession | null>(null);
  const stateRef = useRef(state);
  const mediaRef = useRef<MediaSession | null>(null);
  const rtcRef = useRef<ActiveRtcSession | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const terminalSendClaimsRef = useRef(new Set<string>());
  const disposedRef = useRef(false);
  const dispatchRef = useRef<(action: CallAction) => Promise<void>>(
    async () => undefined,
  );

  const applyAction = useCallback((action: CallAction) => {
    const reduction = reduceCall(stateRef.current, action);
    if (reduction.state !== stateRef.current) {
      stateRef.current = reduction.state;
      if (!disposedRef.current) setState(reduction.state);
    }
    return reduction.effects;
  }, []);

  const clearRingTimeout = useCallback(() => {
    if (timeoutRef.current === null) return;
    clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }, []);

  const replaceMedia = useCallback((next: MediaSession | null) => {
    mediaRef.current = next;
    if (!disposedRef.current) setMedia(next);
  }, []);

  const releaseMedia = useCallback(
    (operationNumber?: number) => {
      const current = mediaRef.current;
      if (
        !current ||
        (operationNumber !== undefined &&
          current.operationNumber !== operationNumber)
      ) {
        return;
      }
      replaceMedia(null);
    },
    [replaceMedia],
  );

  const takeRtc = useCallback((operationNumber?: number) => {
    const current = rtcRef.current;
    if (
      !current ||
      (operationNumber !== undefined &&
        current.operationNumber !== operationNumber)
    ) {
      return null;
    }
    rtcRef.current = null;
    return current.handle;
  }, []);

  const isCurrentOperation = useCallback(
    (operationNumber: number, callId?: string) => {
      const current = stateRef.current.current;
      return (
        !disposedRef.current &&
        current?.operationNumber === operationNumber &&
        (callId === undefined || current.callId === callId)
      );
    },
    [],
  );

  const leaveRtc = useCallback(
    async (operationNumber: number) => {
      const rtc = takeRtc(operationNumber);
      if (!rtc) return;
      try {
        await rtc.leave(5000);
      } catch {
        applyAction({
          type: 'effect-failed',
          failure: 'matrixrtc-leave',
          operationNumber,
        });
      }
    },
    [applyAction, takeRtc],
  );

  const finishCall = useCallback(
    async (roomId: string, operationNumber: number) => {
      releaseMedia(operationNumber);
      await leaveRtc(operationNumber);
      const current = stateRef.current.current;
      if (
        current?.roomId === roomId &&
        current.operationNumber === operationNumber
      ) {
        applyAction({ type: 'cleanup', operationNumber });
        clearRingTimeout();
      }
    },
    [applyAction, clearRingTimeout, leaveRtc, releaseMedia],
  );

  const sendTerminalOnce = useCallback(
    (effect: TerminalWriteEffect, operationNumber: number) => {
      const key = `${effect.roomId}\u0000${effect.record.callId}`;
      const claims = terminalSendClaimsRef.current;
      if (claims.has(key)) return;
      if (claims.size >= 256) {
        const oldest = claims.values().next().value;
        if (oldest) claims.delete(oldest);
      }
      claims.add(key);
      void sendCallTerminal(effect.roomId, effect.record).catch(() => {
        if (disposedRef.current) return;
        applyAction({
          type: 'effect-failed',
          failure: 'terminal-send',
          operationNumber,
        });
      });
    },
    [applyAction, sendCallTerminal],
  );

  const runEffects = useCallback(
    async (effects: CallEffect[], operationNumber: number) => {
      for (const effect of effects) {
        if (effect.type === 'start-timeout') {
          clearRingTimeout();
          timeoutRef.current = setTimeout(() => {
            timeoutRef.current = null;
            void dispatchRef.current({
              type: 'timeout',
              callId: effect.callId,
              operationNumber: effect.operationNumber,
              at: Date.now(),
            });
          }, effect.delayMs);
          continue;
        }
        if (effect.type === 'clear-timeout') {
          clearRingTimeout();
          continue;
        }
        if (effect.type === 'write-terminal') {
          sendTerminalOnce(effect, operationNumber);
          continue;
        }
        if (effect.type === 'end-local-audio') {
          releaseMedia(effect.operationNumber);
          continue;
        }
        await finishCall(effect.roomId, effect.operationNumber);
      }
    },
    [clearRingTimeout, finishCall, releaseMedia, sendTerminalOnce],
  );

  const transition = useCallback(
    (action: CallAction) => {
      const effects = applyAction(action);
      const operationNumber =
        stateRef.current.current?.operationNumber ??
        stateRef.current.operationNumber;
      return runEffects(effects, operationNumber);
    },
    [applyAction, runEffects],
  );
  dispatchRef.current = transition;

  const requestMedia = useCallback(
    async (
      roomId: string,
      peerId: string,
      callId: string,
      operationNumber: number,
    ) => {
      try {
        const response = await api.getLiveKitToken({ roomId });
        if (!isCurrentOperation(operationNumber, callId)) return;
        if (
          !response.success ||
          !response.data?.token ||
          !response.data.livekitUrl
        ) {
          throw new Error('LiveKit token is unavailable.');
        }
        replaceMedia({
          callId,
          operationNumber,
          peerId,
          roomId,
          token: response.data.token,
          url: response.data.livekitUrl,
        });
      } catch {
        if (isCurrentOperation(operationNumber, callId)) {
          await transition({ type: 'fail', operationNumber, at: Date.now() });
        }
      }
    },
    [api, isCurrentOperation, replaceMedia, transition],
  );

  const startCall = useCallback(
    async (roomId: string, operatorId: number) => {
      if (
        stateRef.current.phase !== 'idle' ||
        commsStatus !== 'ready' ||
        !matrixUserId ||
        directRooms.get(operatorId) !== roomId
      ) {
        return;
      }
      const operationNumber = stateRef.current.operationNumber + 1;
      applyAction({ type: 'operation-reserved', operationNumber });
      let rtc: MatrixRtcSessionHandle;
      let peerId: string | undefined;
      let callId: string;
      try {
        rtc = joinRtcCall(roomId, liveKitServiceUrl);
        rtcRef.current = { handle: rtc, operationNumber };
        peerId = rtc.getPeerId();
        callId = createCallId();
        if (!peerId) throw new Error('Direct call peer is unavailable.');
      } catch {
        applyAction({
          type: 'effect-failed',
          failure: 'rtc-setup',
          operationNumber,
        });
        await leaveRtc(operationNumber);
        return;
      }
      await transition({
        type: 'outgoing-started',
        callId,
        roomId,
        initiatorId: matrixUserId,
        peerId,
        operatorId,
        startedTs: Date.now(),
        operationNumber,
      });
      try {
        await rtc.sendRing(callId, peerId);
      } catch {
        await transition({ type: 'fail', operationNumber, at: Date.now() });
        return;
      }
      if (isCurrentOperation(operationNumber, callId)) {
        await requestMedia(roomId, peerId, callId, operationNumber);
      }
    },
    [
      applyAction,
      commsStatus,
      createCallId,
      directRooms,
      isCurrentOperation,
      joinRtcCall,
      leaveRtc,
      liveKitServiceUrl,
      matrixUserId,
      requestMedia,
      transition,
    ],
  );

  const acceptCall = useCallback(async () => {
    const current = stateRef.current.current;
    if (
      stateRef.current.phase !== 'incoming-ringing' ||
      !current ||
      commsStatus !== 'ready'
    ) {
      return;
    }
    await transition({
      type: 'accept',
      operationNumber: current.operationNumber,
      at: Date.now(),
    });
    if (!isCurrentOperation(current.operationNumber, current.callId)) return;
    try {
      const rtc = joinRtcCall(current.roomId, liveKitServiceUrl);
      rtcRef.current = {
        handle: rtc,
        operationNumber: current.operationNumber,
      };
      await requestMedia(
        current.roomId,
        current.peerId,
        current.callId,
        current.operationNumber,
      );
    } catch {
      applyAction({
        type: 'effect-failed',
        failure: 'rtc-setup',
        operationNumber: current.operationNumber,
      });
      await transition({
        type: 'fail',
        operationNumber: current.operationNumber,
        at: Date.now(),
      });
    }
  }, [
    applyAction,
    commsStatus,
    isCurrentOperation,
    joinRtcCall,
    liveKitServiceUrl,
    requestMedia,
    transition,
  ]);

  const declineCall = useCallback(async () => {
    const current = stateRef.current.current;
    if (stateRef.current.phase !== 'incoming-ringing' || !current) return;
    await transition({
      type: 'decline',
      operationNumber: current.operationNumber,
      at: Date.now(),
    });
  }, [transition]);

  const hangUp = useCallback(async () => {
    const current = stateRef.current.current;
    if (!current) return;
    const type =
      stateRef.current.phase === 'outgoing-ringing'
        ? 'cancel'
        : stateRef.current.phase === 'connecting' &&
            current.direction === 'incoming'
          ? 'fail'
          : 'hangup';
    await transition({
      type,
      operationNumber: current.operationNumber,
      at: Date.now(),
    });
  }, [transition]);

  const handlePresence = useCallback(
    (
      watchedRoomId: string,
      operatorId: number,
      presence: MatrixRtcPresence,
    ) => {
      const event = presence.protocolEvent;
      const canonicalPeerId = presence.peerId;
      if (
        event?.kind === 'ring' &&
        event.roomId === watchedRoomId &&
        event.peerId === matrixUserId &&
        canonicalPeerId !== undefined &&
        event.senderId === canonicalPeerId
      ) {
        void transition({
          type: 'incoming-ring',
          callId: event.callId,
          roomId: watchedRoomId,
          initiatorId: event.senderId,
          peerId: canonicalPeerId,
          operatorId,
          startedTs: event.originServerTs,
          eventId: event.eventId,
          originServerTs: event.originServerTs,
        });
      }
      if (
        event?.kind === 'terminal' &&
        event.roomId === watchedRoomId &&
        matrixUserId &&
        canonicalPeerId &&
        isValidCallRecordWriter(event.record, event.senderId, {
          localUserId: matrixUserId,
          peerUserId: canonicalPeerId,
        })
      ) {
        void transition({
          type: 'terminal-observed',
          roomId: event.roomId,
          senderId: event.senderId,
          localUserId: matrixUserId,
          eventId: event.eventId,
          originServerTs: event.originServerTs,
          record: event.record,
        });
      }
      const current = stateRef.current.current;
      if (!current || current.roomId !== watchedRoomId) return;
      const remoteMemberships = presence.remoteMemberships.filter(
        (membership) => membership.userId === current.peerId,
      );
      remoteMemberships.forEach((membership) => {
        void transition({
          type: 'membership-observed',
          callId: current.callId,
          senderId: membership.userId,
          eventId: membership.eventId,
          operationNumber: current.operationNumber,
        });
      });
      if (
        remoteMemberships.length === 0 &&
        current.remoteMembershipReady === true
      ) {
        void transition({
          type: 'peer-membership-lost',
          callId: current.callId,
          senderId: current.peerId,
          operationNumber: current.operationNumber,
          at: Date.now(),
        });
      }
    },
    [matrixUserId, transition],
  );

  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (commsStatus !== 'ready' && commsStatus !== 'reconnecting') {
      return () => undefined;
    }
    const unsubscribes = [...directRooms].map(([operatorId, roomId]) =>
      watchRoomCall(roomId, (presence) =>
        handlePresence(roomId, operatorId, presence),
      ),
    );
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [commsStatus, directRooms, handlePresence, watchRoomCall]);

  useEffect(() => {
    if (commsStatus !== 'stopping' && commsStatus !== 'idle') return;
    const current = stateRef.current.current;
    if (!current) return;
    clearRingTimeout();
    releaseMedia(current.operationNumber);
    const rtc = takeRtc(current.operationNumber);
    applyAction({
      type: 'cleanup',
      operationNumber: current.operationNumber,
    });
    if (rtc) void rtc.leave(5000).catch(() => undefined);
  }, [applyAction, clearRingTimeout, commsStatus, releaseMedia, takeRtc]);

  useEffect(
    () => () => {
      disposedRef.current = true;
      clearRingTimeout();
      releaseMedia();
      const rtc = takeRtc();
      if (rtc) void rtc.leave(5000).catch(() => undefined);
    },
    [clearRingTimeout, releaseMedia, takeRtc],
  );

  const mediaIsCurrent =
    media !== null &&
    state.current?.callId === media.callId &&
    state.current.operationNumber === media.operationNumber;
  const renderMedia =
    mediaIsCurrent &&
    state.phase !== 'idle' &&
    state.phase !== 'ending' &&
    state.phase !== 'failed';
  const publishAudio =
    renderMedia &&
    state.current !== undefined &&
    (state.current.direction === 'incoming' ||
      state.current.remoteMembershipReady === true);

  const handleMediaAction = useCallback(
    (
      session: MediaSession,
      action: 'connected' | 'complete' | 'reconnecting' | 'recovered' | 'fail',
    ) => {
      if (!isCurrentOperation(session.operationNumber, session.callId)) return;
      const current = stateRef.current.current;
      if (!current) return;
      if (
        action === 'complete' &&
        (current.direction !== 'outgoing' ||
          current.remoteMembershipReady !== true ||
          (stateRef.current.phase !== 'active' &&
            stateRef.current.phase !== 'reconnecting'))
      ) {
        return;
      }
      if (
        action === 'fail' &&
        (stateRef.current.phase === 'ending' ||
          stateRef.current.phase === 'failed')
      ) {
        return;
      }
      const at = Date.now();
      if (action === 'connected') {
        void transition({
          type: 'connected',
          operationNumber: session.operationNumber,
          at,
        });
      } else if (action === 'complete') {
        void transition({
          type: 'complete',
          operationNumber: session.operationNumber,
          at,
        });
      } else if (action === 'reconnecting') {
        void transition({
          type: 'reconnecting',
          operationNumber: session.operationNumber,
        });
      } else if (action === 'recovered') {
        void transition({
          type: 'recovered',
          operationNumber: session.operationNumber,
        });
      } else {
        void transition({
          type: 'fail',
          operationNumber: session.operationNumber,
          at,
        });
      }
    },
    [isCurrentOperation, transition],
  );

  const value = useMemo<CallContextValue>(() => {
    const current = state.current;
    return {
      phase: state.phase,
      status: statusFor(state),
      direction: current?.direction ?? null,
      roomId: current?.roomId ?? null,
      operatorId: current?.operatorId ?? null,
      error:
        state.phase === 'failed' ? 'Voice call could not be connected.' : null,
      failureCategory: state.lastFailure ?? null,
      result: state.terminalPresentation?.outcome ?? null,
      resultDirection: state.terminalPresentation?.direction ?? null,
      startCall,
      acceptCall,
      declineCall,
      hangUp,
      returnToConversation: () => {
        const active = stateRef.current.current;
        if (active) {
          onReturnToConversation?.(active.operatorId, active.roomId);
        }
      },
      dismissResult: () => {
        void transition({ type: 'dismiss-terminal' });
      },
    };
  }, [
    acceptCall,
    declineCall,
    hangUp,
    onReturnToConversation,
    startCall,
    state,
    transition,
  ]);

  return (
    <CallContext.Provider value={value}>
      {children}
      {renderMedia && media ? (
        <LiveKitCallMedia
          expectedPeerIdentity={media.peerId}
          onDisconnected={() => handleMediaAction(media, 'fail')}
          onError={() => handleMediaAction(media, 'fail')}
          onReconnected={() => handleMediaAction(media, 'recovered')}
          onReconnecting={() => handleMediaAction(media, 'reconnecting')}
          onRemoteAudioEnded={() => handleMediaAction(media, 'complete')}
          onRemoteAudioReady={() => handleMediaAction(media, 'connected')}
          publishAudio={publishAudio}
          token={media.token}
          url={media.url}
        />
      ) : null}
    </CallContext.Provider>
  );
};
